import { Fragment, useMemo, useRef, useState } from 'react'
import { ChevronRight, History } from 'lucide-react'
import { ActualHistory } from '@/components/custom/ogsm/actual-history'
import { ScorecardDatePicker } from '@/components/custom/scorecard/scorecard-date-picker'
import { ScorecardTargetValueInput } from '@/components/custom/scorecard/scorecard-target-value-input'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography, typographyVariants } from '@/components/ui/typography'
import {
  actualEntryRows,
  shouldSaveActual,
  todayIsoDate,
  type ActualDraft,
  type ActualEntryRow,
} from '@/features/ogsm/actual-entry'
import { ogsmErrorMessage } from '@/features/ogsm/errors'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { toScorecardDataType } from '@/features/ogsm/target-format'
import { useOgsmApi } from '@/hooks/use-ogsm'
import { useAddOgsmActualMutation } from '@/hooks/use-ogsm-data'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { OgsmBoard, OgsmBoardNode } from '@/types/api'

export interface EnterActualsDialogProps {
  companyId: string
  board: OgsmBoard
  open: boolean
  onClose: () => void
}

interface RowState {
  draft: ActualDraft
  prefill: ActualDraft
  status: 'idle' | 'saving' | 'saved' | 'error'
  error: unknown
}

const COLUMNS = 'md:grid-cols-[minmax(0,1fr)_12rem_11rem_2.25rem]'
const HISTORY_PREVIEW = 5

type RowDivider = 'none' | 'row' | 'group'
const DIVIDER_CLASS: Record<RowDivider, string> = {
  none: '',
  row: 'border-t border-primary/10',
  group: 'border-t border-primary/20',
}

function initialRow(node: OgsmBoardNode, today: string): RowState {
  const prefill = {
    value: node.latestActual?.value ?? Number.NaN,
    asOfDate: today,
  }
  return { draft: prefill, prefill, status: 'idle', error: null }
}

function RowPath({ ancestors }: { ancestors: OgsmBoardNode[] }) {
  return (
    <Typography
      as="p"
      variant="body-sm"
      className="flex min-w-0 items-center text-primary/60"
    >
      <span className="sr-only">{OGSM_COPY.pathUnder} </span>
      {ancestors.map((ancestor, index) => (
        <Fragment key={ancestor.id}>
          {index > 0 && <span className="sr-only">, </span>}
          <span
            title={ancestor.title}
            className={cn(
              'truncate',
              // Earlier segments keep up to 2/5 each so a short one never collapses to "T…".
              index < ancestors.length - 1 ? 'max-w-2/5 shrink-0' : 'min-w-0'
            )}
          >
            {ancestor.title}
          </span>
          <ChevronRight
            aria-hidden
            className="mx-1 size-3 shrink-0 text-primary/40"
          />
        </Fragment>
      ))}
    </Typography>
  )
}

function ActualEntryForm({
  companyId,
  row,
  divider,
  state,
  onChange,
  onSave,
}: {
  companyId: string
  row: ActualEntryRow
  divider: RowDivider
  state: RowState
  onChange: (draft: ActualDraft) => void
  onSave: () => void
}) {
  const { node, ancestors } = row
  const [historyOpen, setHistoryOpen] = useState(false)
  const latest = node.latestActual
  const saving = state.status === 'saving'
  const valueId = `enter-actuals-value-${node.id}`
  const dateId = `enter-actuals-date-${node.id}`
  const nested = ancestors.length > 0
  const newerNote =
    latest && state.draft.asOfDate < latest.asOfDate
      ? OGSM_COPY.newerActualShown(formatDate(latest.asOfDate))
      : null

  return (
    <Collapsible asChild open={historyOpen} onOpenChange={setHistoryOpen}>
      <form
        aria-label={node.title}
        className={cn(
          'grid grid-cols-2 items-center gap-x-4 gap-y-2 py-3',
          COLUMNS,
          DIVIDER_CLASS[divider]
        )}
        onSubmit={(event) => {
          event.preventDefault()
          onSave()
        }}
      >
        <div className="col-span-2 col-start-1 row-start-1 flex min-w-0 flex-col gap-0.5 pr-11 md:col-span-1 md:pr-0">
          {nested && <RowPath ancestors={ancestors} />}
          <Typography
            as="span"
            variant="h6"
            className={cn(
              'break-words leading-snug',
              !nested && 'font-semibold'
            )}
          >
            {node.title}
          </Typography>
          {!nested && node.description && (
            <Typography variant="body-sm" className="text-primary/60">
              {node.description}
            </Typography>
          )}
        </div>
        <div className="min-w-0">
          <Label htmlFor={valueId} className="sr-only">
            {OGSM_COPY.latestActualFor(node.title)}
          </Label>
          <ScorecardTargetValueInput
            id={valueId}
            value={state.draft.value}
            onChange={(value) => onChange({ ...state.draft, value })}
            dataType={toScorecardDataType(node.targetType)}
            unitLabel={node.targetType === 'NUMBER' ? node.descriptor : null}
            disabled={saving}
          />
        </div>
        <div className="min-w-0">
          <Label htmlFor={dateId} className="sr-only">
            {OGSM_COPY.asOfFor(node.title)}
          </Label>
          <ScorecardDatePicker
            id={dateId}
            value={state.draft.asOfDate}
            onChange={(asOfDate) => onChange({ ...state.draft, asOfDate })}
            disabled={saving}
          />
        </div>
        <Tooltip>
          <CollapsibleTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={OGSM_COPY.historyFor(node.title)}
                className="col-start-2 row-start-1 self-start justify-self-end text-primary/60 aria-expanded:bg-primary/5 aria-expanded:text-primary md:col-start-4 md:self-center md:justify-self-auto"
              >
                <History />
              </Button>
            </TooltipTrigger>
          </CollapsibleTrigger>
          <TooltipContent>{OGSM_COPY.actualHistory}</TooltipContent>
        </Tooltip>
        {(newerNote ||
          state.status === 'error' ||
          state.status === 'saved') && (
          <div className="col-span-2 flex flex-col gap-1 md:col-span-2 md:col-start-2">
            {newerNote && (
              <Typography variant="body-sm" className="text-primary/60">
                {newerNote}
              </Typography>
            )}
            {state.status === 'error' && (
              <Typography
                variant="body-sm"
                className="text-destructive"
                role="alert"
              >
                {ogsmErrorMessage(state.error)}
              </Typography>
            )}
            {state.status === 'saved' && (
              <Typography
                variant="body-sm"
                className="text-primary/60"
                role="status"
              >
                {OGSM_COPY.actualSaved}
              </Typography>
            )}
          </div>
        )}
        <CollapsibleContent className="col-span-2 grid grid-cols-subgrid gap-y-1 rounded-md bg-primary/5 py-2 md:col-start-2">
          <ActualHistory
            companyId={companyId}
            node={node}
            layout="columns"
            limit={HISTORY_PREVIEW}
          />
        </CollapsibleContent>
      </form>
    </Collapsible>
  )
}

function EnterActualsBody({
  companyId,
  board,
  onClose,
}: Omit<EnterActualsDialogProps, 'open'>) {
  const rows = useMemo(() => actualEntryRows(board), [board])
  const [today] = useState(todayIsoDate)
  const [states, setStates] = useState<Record<string, RowState>>({})
  const add = useAddOgsmActualMutation(useOgsmApi(companyId), board.session.id)
  // Saves still in flight, so Done waits for them instead of closing over a pending POST.
  const inFlight = useRef(new Map<string, Promise<boolean>>())

  const stateOf = (node: OgsmBoardNode) =>
    states[node.id] ?? initialRow(node, today)
  const update = (node: OgsmBoardNode, next: Partial<RowState>) =>
    setStates((current) => ({
      ...current,
      [node.id]: { ...(current[node.id] ?? initialRow(node, today)), ...next },
    }))
  const pending = (row: ActualEntryRow) => {
    const state = stateOf(row.node)
    return (
      state.status !== 'saving' &&
      shouldSaveActual(row.node.latestActual, state.prefill, state.draft)
    )
  }

  const save = (row: ActualEntryRow): Promise<boolean> => {
    const { draft } = stateOf(row.node)
    update(row.node, { status: 'saving', error: null })
    const request = (async () => {
      try {
        await add.mutateAsync({ nodeId: row.node.id, input: draft })
        update(row.node, { status: 'saved', prefill: draft })
        return true
      } catch (error) {
        update(row.node, { status: 'error', error })
        return false
      } finally {
        inFlight.current.delete(row.node.id)
      }
    })()
    inFlight.current.set(row.node.id, request)
    return request
  }

  const done = async () => {
    const running = rows.flatMap(
      (row) => inFlight.current.get(row.node.id) ?? []
    )
    const results = await Promise.allSettled([
      ...running,
      ...rows.filter(pending).map(save),
    ])
    if (
      results.every((result) => result.status === 'fulfilled' && result.value)
    )
      onClose()
  }

  return (
    <>
      <div className="flex min-h-0 flex-col overflow-y-auto px-5 pb-5">
        {rows.length === 0 ? (
          <Typography variant="muted" className="py-8 text-center">
            {OGSM_COPY.enterActualsEmpty}
          </Typography>
        ) : (
          <>
            <div
              className={cn(
                'sticky top-0 z-10 hidden gap-x-4 border-b border-primary/10 bg-background py-3 md:grid',
                COLUMNS
              )}
            >
              <Typography variant="overline" className="text-primary/60">
                {OGSM_COPY.enterActualsColumns.metric}
              </Typography>
              <Typography variant="overline" className="text-primary/60">
                {OGSM_COPY.enterActualsColumns.latestActual}
              </Typography>
              <Typography variant="overline" className="text-primary/60">
                {OGSM_COPY.enterActualsColumns.asOf}
              </Typography>
              <Typography as="span" className="sr-only">
                {OGSM_COPY.actualHistory}
              </Typography>
            </div>
            <TooltipProvider delayDuration={300}>
              {rows.map((row, index) => (
                <ActualEntryForm
                  key={row.node.id}
                  companyId={companyId}
                  row={row}
                  divider={
                    index === 0
                      ? 'none'
                      : row.rootId !== rows[index - 1]?.rootId
                        ? 'group'
                        : 'row'
                  }
                  state={stateOf(row.node)}
                  onChange={(draft) => {
                    if (stateOf(row.node).status === 'saving') return
                    update(row.node, { draft, status: 'idle', error: null })
                  }}
                  onSave={() => {
                    if (pending(row)) void save(row)
                  }}
                />
              ))}
            </TooltipProvider>
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-4 border-t border-primary/10 bg-muted px-5 py-4">
        <Typography variant="body-sm" className="text-primary/60">
          {OGSM_COPY.enterActualsFooter}
        </Typography>
        <Button onClick={() => void done()}>{OGSM_COPY.done}</Button>
      </div>
    </>
  )
}

/**
 * Enters actuals for every goal on the board that takes one (`actualEditable`),
 * at every depth. Enter saves a row; Done saves every changed row and closes
 * once all of them succeed.
 */
export function EnterActualsDialog({
  companyId,
  board,
  open,
  onClose,
}: EnterActualsDialogProps) {
  const opener = useRef<HTMLElement | null>(null)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        className="flex max-h-[90dvh] max-w-4xl flex-col gap-0 overflow-hidden p-0"
        onOpenAutoFocus={() => {
          const active = document.activeElement
          opener.current = active instanceof HTMLElement ? active : null
        }}
        onCloseAutoFocus={(event) => {
          // Radix returns focus only to a DialogTrigger, and the opener is a plain button.
          if (!opener.current?.isConnected) return
          event.preventDefault()
          opener.current.focus()
        }}
      >
        <DialogHeader className="gap-1.5 border-b border-primary/10 px-5 py-4 pr-12 text-left">
          <DialogTitle className={typographyVariants({ variant: 'h3' })}>
            {OGSM_COPY.enterActuals}
          </DialogTitle>
          <DialogDescription
            className={cn(
              typographyVariants({ variant: 'body-sm' }),
              'text-primary/60'
            )}
          >
            {OGSM_COPY.enterActualsHint}
          </DialogDescription>
        </DialogHeader>
        {/* Radix mounts the content per open, so every open starts from the current board. */}
        <EnterActualsBody
          companyId={companyId}
          board={board}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  )
}
